import _ from 'lodash';
import classnames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { AgentAvailability } from 'models/agent_status';
import { AgentStatusCard, AgentStatusCardColumns } from './charts/agent_status_card';
import AgentsLiveboardFilterBar from './agents_liveboard_filter_bar';
import { activeFilterCount, EMPTY_FILTERS, filterAgents } from './agents_liveboard_filtering';
import connect from 'components/lib/connect';
import { Device } from 'scripts/lib/device_detector';
import { EndpointTypes } from 'models/endpoint';
import i18n from 'scripts/i18n/i18n';
import InteractionType from 'models/interaction_type';
import MobileSectionTabs from './mobile_section_tabs';
import { PreferencesStatus, RoutingChannel } from 'models/agent_routing_preferences';
import { WorkType } from 'models/liveboards/agents_liveboard';
import useDevice from 'components/hooks/use_device';

const WORKING = 'working';
const AWAY = 'away';

export function AgentsLiveboard({
  canUpdateAgentStatus,
  isAgentFiltersEnabled,
  shouldDisplayFocus,
  idleAndAwayAgents,
  isChatEnabled,
  workingAgents,
}) {
  const isPhone = useDevice() === Device.Phone;
  const { t } = useTranslation();
  const [section, setSection] = useState(WORKING);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const filteredWorking = useMemo(
    () => (isAgentFiltersEnabled ? filterAgents(workingAgents, filters) : workingAgents),
    [isAgentFiltersEnabled, workingAgents, filters]
  );
  const filteredAway = useMemo(
    () => (isAgentFiltersEnabled ? filterAgents(idleAndAwayAgents, filters) : idleAndAwayAgents),
    [isAgentFiltersEnabled, idleAndAwayAgents, filters]
  );

  const showEmptyState = isAgentFiltersEnabled && activeFilterCount(filters) > 0;

  return (
    <div className={classnames('agentsLiveboard', 'liveboards-board', 'liveboards-agents')}>
      {renderUtilizationStats()}
    </div>
  );

  function renderUtilizationStats() {
    let fields = [
      AgentStatusCardColumns.AVAILABILITY,
      AgentStatusCardColumns.NAME,
      AgentStatusCardColumns.CURRENT,
      canUpdateAgentStatus ? AgentStatusCardColumns.ACTIVE_REASON_EDITABLE : AgentStatusCardColumns.ACTIVE_REASON,
      AgentStatusCardColumns.DURATION,
    ];

    if (shouldDisplayFocus) {
      fields.splice(1, 0, AgentStatusCardColumns.FOCUS);
    }

    const workingCard = (
      <WorkingAgentsCard
        data={filteredWorking}
        fields={fields}
        filterControl={
          isAgentFiltersEnabled ? <AgentsLiveboardFilterBar filters={filters} onChange={setFilters} /> : null
        }
        isChatEnabled={isChatEnabled}
        shouldDisplayFocus={shouldDisplayFocus}
        showEmptyState={showEmptyState}
        showExpandedSort={isAgentFiltersEnabled}
        showSortControl
        title={t('Working Agent', { count: filteredWorking.length })}
      />
    );

    const awayCard = (
      <AgentStatusCard
        data={filteredAway}
        fields={[
          AgentStatusCardColumns.NAME,
          canUpdateAgentStatus ? AgentStatusCardColumns.AWAY_REASON_EDITABLE : AgentStatusCardColumns.AWAY_REASON,
          AgentStatusCardColumns.DURATION,
        ]}
        showEmptyState={showEmptyState}
        title={t('Away Agent', { count: filteredAway.length })}
      />
    );

    if (isPhone) {
      return (
        <div className="agentsLiveboard-statuses">
          <MobileSectionTabs
            onSelect={setSection}
            options={[
              { key: WORKING, label: t('Working'), count: filteredWorking.length },
              { key: AWAY, label: t('Away'), count: filteredAway.length },
            ]}
            value={section}
          />
          {section === WORKING ? workingCard : awayCard}
        </div>
      );
    }

    return (
      <div className="agentsLiveboard-statuses">
        {workingCard}
        {awayCard}
      </div>
    );
  }
}

AgentsLiveboard.propTypes = {
  canUpdateAgentStatus: PropTypes.bool,
  isAgentFiltersEnabled: PropTypes.bool,
  idleAndAwayAgents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      availableChannels: PropTypes.arrayOf(PropTypes.oneOf(_.values(RoutingChannel))),
      duration: PropTypes.number,
      awayReason: PropTypes.string,
      awayReasonId: PropTypes.string,
    })
  ),
  shouldDisplayFocus: PropTypes.bool,
  isChatEnabled: PropTypes.bool,
  workingAgents: PropTypes.arrayOf(
    PropTypes.shape({
      activeReason: PropTypes.string,
      currentlyOn: PropTypes.shape({
        call: PropTypes.bool,
        activity: PropTypes.string,
        activeSessions: PropTypes.arrayOf(
          PropTypes.shape({
            conversationId: PropTypes.string,
            customerId: PropTypes.string,
            startedAt: PropTypes.string,
            sessionTypes: PropTypes.arrayOf(PropTypes.string),
            slaDueAt: PropTypes.string,
          })
        ),
        sessionWork: PropTypes.shape({
          customerId: PropTypes.string,
          sessionType: PropTypes.oneOf(_.values(InteractionType)),
          workType: PropTypes.oneOf(_.values(WorkType)),
        }),
      }),
      duration: PropTypes.number,
      id: PropTypes.string,
      name: PropTypes.string,
      availableChannels: PropTypes.arrayOf(PropTypes.oneOf(_.values(RoutingChannel))),
      routingPreferences: PropTypes.shape({
        status: PropTypes.oneOf(_.values(PreferencesStatus)),
        updatedAt: PropTypes.string,
      }),
    })
  ),
};

function mapStateToProps({ getProvider, isFeatureEnabled }) {
  const agentsProvider = getProvider('agents');
  const agentStatusReasonProvider = getProvider('agentStatusReasons');
  const liveboardProvider = getProvider('liveboard');
  const liveboard = liveboardProvider.getPending() || liveboardProvider.get() || {};

  const messagingConfiguration = getProvider('messagingConfiguration').get();
  const shouldDisplayFocus = messagingConfiguration && messagingConfiguration.enableFocusMode;

  const statusGroups = getStatusGroups();

  const channelConfiguration = getProvider('channelConfiguration').get();
  const isChatEnabled = channelConfiguration && channelConfiguration.isChannelEnabled(EndpointTypes.CHAT);

  const canUpdateAgentStatus = isFeatureEnabled('agentStatusControl');
  const isAgentFiltersEnabled = isFeatureEnabled('liveboardAgentFilters');

  return {
    canUpdateAgentStatus,
    isAgentFiltersEnabled,
    shouldDisplayFocus,
    idleAndAwayAgents: statusGroups.idleAndAway,
    isChatEnabled,
    workingAgents: statusGroups.working,
  };

  function getAgentStatusReason(reasonId) {
    return _.get(agentStatusReasonProvider.findBy({ id: reasonId }), 'name') || i18n.t('Loading...');
  }

  function getStatusGroups() {
    let utilStats = liveboard.utilization || [];

    let nameStats = utilStats.map(utilStat => {
      const agent = agentsProvider.findBy({ id: utilStat.agentId });
      return _.extend(utilStat.toJs(), { name: (agent && agent.getDisplayName()) || 'Agent' });
    });

    let idleAndAway = [];
    let working = [];

    nameStats.forEach(stats => {
      const isAway = stats.status === AgentAvailability.AWAY;
      if (isAway) {
        idleAndAway.push({
          id: stats.agentId,
          name: stats.name,
          availableChannels: stats.availableChannels,
          duration: stats.statusReasonId
            ? moment().diff(stats.statusUpdatedAt, 'ms')
            : moment().diff(stats.presenceUpdatedAt, 'ms'),
          awayReason: stats.statusReasonId ? getAgentStatusReason(stats.statusReasonId) : i18n.t('Idle'),
          awayReasonId: stats.statusReasonId,
        });
      } else if (stats.status !== AgentAvailability.OFFLINE) {
        let currentlyOn = {
          call: !!stats.onCall,
          activity: stats.activity,
          activeSessions: stats.activeSessions,
        };

        if (stats.sessionWork) {
          currentlyOn.sessionWork = stats.sessionWork;
        }

        if (currentlyOn.activity) {
          working.push({
            currentlyOn,
            id: stats.agentId,
            name: stats.name,
            availableChannels: stats.availableChannels,
            routingPreferences: stats.routingPreferences,
            activeReason: stats.statusReasonId ? getAgentStatusReason(stats.statusReasonId) : '',
            duration: stats.statusUpdatedAt ? moment().diff(stats.statusUpdatedAt, 'ms') : undefined,
          });
        }
      }
    });

    idleAndAway = _.sortBy(idleAndAway, agent => agent.name.toLowerCase());
    working = _.sortBy(working, agent => agent.name.toLowerCase());

    return {
      working,
      idleAndAway,
    };
  }
}

const AgentsLiveboardContainer = connect(mapStateToProps)(AgentsLiveboard);

export default AgentsLiveboardContainer;

export const WorkingAgentsCard = styled(AgentStatusCard)`
  flex: ${p => (p.isChatEnabled ? 3 : 2)} 0 205px;
`;
